import React from 'react';
import type { CFCommittee } from '../../types/campaignFinance';
import { getPartyHex } from '../../utils/campaignFinanceUtils';

interface CFCommitteeHeaderProps {
  committee: CFCommittee;
  /** Candidate party, if resolved from contribution rows (the spine has no party). */
  party?: string | null;
}

const badge = 'inline-flex items-center rounded border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary text-accent-500 dark:text-text-dark-tertiary rounded-[5px] px-[7px] py-px text-[10px] font-semibold';

const fmtMonthYear = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/** Committee identity header — name, type, status, candidate link, source badges. */
const CFCommitteeHeader: React.FC<CFCommitteeHeaderProps> = ({ committee, party }) => {
  const registered = fmtMonthYear(committee.first_seen);
  // "Active" if seen within the last ~14 months, else show the dormant state.
  const lastSeen = committee.last_seen ? new Date(committee.last_seen) : null;
  const active = !!lastSeen && (Date.now() - lastSeen.getTime()) < 1000 * 60 * 60 * 24 * 425;

  return (
    <div className="card !p-0 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 p-5 px-6 items-start">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-semibold text-[23px] tracking-[-0.02em] text-primary-800 dark:text-text-dark-primary">
              {committee.current_name || 'Unknown committee'}
            </span>
            {committee.current_type && (
              <span className="rounded-full bg-primary-100 dark:bg-primary-dark-100/40 text-primary-700 dark:text-primary-dark-400 px-2.5 py-[3px] text-[11px] font-semibold">
                {committee.current_type}
              </span>
            )}
            <span
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px] font-semibold ${
                active
                  ? 'bg-cf-in-soft text-cf-in-value'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-accent-500'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-cf-in' : 'bg-accent-400'}`} />
              {active ? 'Active' : 'Dormant'}
            </span>
          </div>

          <div className="flex items-center gap-2.5 mt-2.5 flex-wrap text-[12.5px] text-accent-500 dark:text-text-dark-tertiary">
            {committee.candidate_name && (
              <span className="flex items-center gap-1.5">
                Candidate
                <span className="w-2 h-2 rounded-full" style={{ background: getPartyHex(party) }} />
                <strong className="text-text-primary dark:text-text-dark-primary">{committee.candidate_name}</strong>
              </span>
            )}
            <span className="tabular-nums">co_id {committee.co_id}</span>
            {registered && (
              <>
                <span className="text-border-strong">·</span>
                <span>Registered {registered}</span>
              </>
            )}
            {committee.source && <span className={badge}>{committee.source}</span>}
            {committee.level && <span className={badge}>{committee.level}</span>}
          </div>

          {committee.purpose && (
            <div className="text-[12.5px] text-text-secondary dark:text-text-dark-secondary mt-2.5 leading-relaxed max-w-[820px]">
              <span className="text-accent-400">Purpose</span>&nbsp;&nbsp;{committee.purpose}
            </div>
          )}

          {committee.name_history && committee.name_history.length > 0 && (
            <div className="text-[12.5px] text-text-secondary dark:text-text-dark-secondary mt-2.5 leading-relaxed">
              <span className="text-accent-400">Formerly</span>{' '}
              {committee.name_history
                .filter(h => h.name && h.name !== committee.current_name)
                .map(h => h.name)
                .slice(0, 3)
                .join(' · ') || '—'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CFCommitteeHeader;
